/**
 * useDetailViewActions - Hook to generate standard action buttons for detail views
 * Provides common actions like copy, delete, export, etc. with consistent behavior
 */
import { useState } from 'react';

export function useDetailViewActions() {
  const [copied, setCopied] = useState(false);

  /**
   * Create a copy action that copies data to clipboard
   */
  const createCopyAction = (data, tooltip = 'Copy as JSON') => {
    const handleCopy = async () => {
      try {
        const jsonString = JSON.stringify(data, null, 2);
        await navigator.clipboard.writeText(jsonString);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    };

    return {
      id: 'copy',
      onClick: handleCopy,
      tooltip,
      customIcon: copied ? (
        <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    };
  };

  /**
   * Create a delete action with confirmation
   */
  const createDeleteAction = (onDelete, confirmMessage = 'Are you sure you want to delete this item? This action cannot be undone.', tooltip = 'Delete') => {
    const handleDelete = async () => {
      if (!confirm(confirmMessage)) {
        return;
      }

      try {
        await onDelete();
      } catch (error) {
        console.error('Failed to delete:', error);
        alert('Failed to delete item');
      }
    };

    return {
      id: 'delete',
      onClick: handleDelete,
      className: "p-2 rounded-lg hover:bg-red-700/50 text-gray-400 hover:text-red-400 transition-all duration-200",
      tooltip,
      icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
    };
  };

  /**
   * Create an export action
   */
  const createExportAction = (data, filename = 'export.json', tooltip = 'Export as file') => {
    const handleExport = () => {
      try {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to export:', error);
      }
    };

    return {
      id: 'export',
      onClick: handleExport,
      tooltip,
      icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
    };
  };

  /**
   * Create a refresh action
   */
  const createRefreshAction = (onRefresh, tooltip = 'Refresh') => {
    return {
      id: 'refresh',
      onClick: onRefresh,
      tooltip,
      icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
    };
  };

  /**
   * Create a custom action
   */
  const createCustomAction = (config) => {
    return {
      id: config.id || 'custom',
      onClick: config.onClick,
      className: config.className,
      tooltip: config.tooltip,
      icon: config.icon,
      customIcon: config.customIcon,
      disabled: config.disabled,
      hidden: config.hidden
    };
  };

  return {
    createCopyAction,
    createDeleteAction,
    createExportAction,
    createRefreshAction,
    createCustomAction
  };
}
